import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  ArrowLeft,
  Shield,
  Send,
  CheckCircle,
  Users,
  Award
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MILITARY_BRANCHES = [
  "Army",
  "Navy",
  "Air Force",
  "Marines",
  "Coast Guard",
  "Space Force"
];

const DISCHARGE_STATUSES = [
  "Honorable",
  "General (Under Honorable Conditions)",
  "Other Than Honorable",
  "Bad Conduct",
  "Entry Level Separation",
  "Medical"
];

const PROFESSIONAL_BACKGROUNDS = [
  "Mental Health Professional",
  "Social Worker",
  "Counselor/Therapist",
  "Healthcare Provider",
  "First Responder (Police/Fire/EMS)",
  "Chaplain/Clergy",
  "Peer Support Specialist",
  "Crisis Hotline Volunteer",
  "Military Family Member",
  "Other"
];

const CERTIFICATIONS = [
  "Peer Support Specialist",
  "Mental Health First Aid",
  "Crisis Intervention Training",
  "Suicide Prevention Training",
  "PTSD Awareness",
  "Substance Abuse Counseling",
  "Trauma-Informed Care",
  "CPR/First Aid",
  "Licensed Clinical Social Worker (LCSW)",
  "Licensed Professional Counselor (LPC)"
];

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const ResponderApplication = ({ user }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    applicant_type: "veteran",
    // Military fields
    military_branch: "",
    years_of_service: "",
    discharge_status: "",
    // Civilian fields
    professional_background: "",
    organization: "",
    years_of_experience: "",
    // Common fields
    certifications: [],
    availability_days: [],
    availability_hours: "",
    zip_code: "",
    bio: "",
    background_check_consent: false
  });

  const handleCertificationToggle = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      availability_days: prev.availability_days.includes(day)
        ? prev.availability_days.filter(d => d !== day)
        : [...prev.availability_days, day]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on applicant type
    if (formData.applicant_type === "veteran") {
      if (!formData.military_branch || !formData.discharge_status) {
        toast.error("Please fill in all required military background fields");
        return;
      }
    } else {
      if (!formData.professional_background) {
        toast.error("Please select your professional background");
        return;
      }
    }
    
    if (!formData.bio) {
      toast.error("Please provide a bio");
      return;
    }
    
    if (!formData.background_check_consent) {
      toast.error("You must consent to a background check");
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        years_of_service: formData.applicant_type === "veteran" ? (parseInt(formData.years_of_service) || 0) : null,
        years_of_experience: formData.applicant_type === "civilian" ? (parseInt(formData.years_of_experience) || 0) : null,
        // Clear irrelevant fields
        military_branch: formData.applicant_type === "veteran" ? formData.military_branch : null,
        discharge_status: formData.applicant_type === "veteran" ? formData.discharge_status : null,
        professional_background: formData.applicant_type === "civilian" ? formData.professional_background : null,
        organization: formData.applicant_type === "civilian" ? formData.organization : null,
      };
      
      await axios.post(`${API}/responders/apply`, submitData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="va-blue-bar h-2"></div>
        <div className="container mx-auto px-4 py-16 max-w-2xl">
          <Card className="card-valor text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                Application Submitted!
              </h2>
              <p className="text-muted-foreground mb-6">
                Thank you for your interest in becoming a VALORLink™ responder. 
                Our team will review your application and contact you soon.
              </p>
              <Button onClick={() => navigate("/dashboard")}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Responder Application
            </span>
            <span className="text-xs text-primary font-bold">™</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Shield className="w-6 h-6 text-primary" />
              Become a VALORLink™ Responder
            </CardTitle>
            <CardDescription>
              Join our network of peer responders helping veterans in crisis. 
              We welcome both veterans and qualified civilians who want to make a difference.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Applicant Type Selection */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  I am applying as a...
                </h3>
                <RadioGroup
                  value={formData.applicant_type}
                  onValueChange={(value) => setFormData({...formData, applicant_type: value})}
                  className="grid sm:grid-cols-2 gap-4"
                >
                  <div className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.applicant_type === "veteran" 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-muted-foreground"
                  }`}>
                    <RadioGroupItem value="veteran" id="veteran" className="mt-1" />
                    <Label htmlFor="veteran" className="ml-3 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Award className="w-5 h-5 text-primary" />
                        <span className="font-semibold">Veteran</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        I have served in the U.S. Armed Forces
                      </p>
                    </Label>
                  </div>
                  
                  <div className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.applicant_type === "civilian" 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-muted-foreground"
                  }`}>
                    <RadioGroupItem value="civilian" id="civilian" className="mt-1" />
                    <Label htmlFor="civilian" className="ml-3 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-5 h-5 text-secondary" />
                        <span className="font-semibold">Qualified Civilian</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        I have professional training or experience in support services
                      </p>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Veteran Military Background */}
              {formData.applicant_type === "veteran" && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                  <h3 className="font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Award className="w-5 h-5 text-primary" />
                    Military Background
                  </h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Branch of Service *</Label>
                      <Select 
                        value={formData.military_branch} 
                        onValueChange={(v) => setFormData({...formData, military_branch: v})}
                      >
                        <SelectTrigger data-testid="branch-select">
                          <SelectValue placeholder="Select branch" />
                        </SelectTrigger>
                        <SelectContent>
                          {MILITARY_BRANCHES.map(branch => (
                            <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="label-uppercase">Years of Service</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        placeholder="e.g., 8"
                        value={formData.years_of_service}
                        onChange={(e) => setFormData({...formData, years_of_service: e.target.value})}
                        data-testid="years-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="label-uppercase">Discharge Status *</Label>
                    <Select 
                      value={formData.discharge_status} 
                      onValueChange={(v) => setFormData({...formData, discharge_status: v})}
                    >
                      <SelectTrigger data-testid="discharge-select">
                        <SelectValue placeholder="Select discharge status" />
                      </SelectTrigger>
                      <SelectContent>
                        {DISCHARGE_STATUSES.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Civilian Professional Background */}
              {formData.applicant_type === "civilian" && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                  <h3 className="font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Users className="w-5 h-5 text-secondary" />
                    Professional Background
                  </h3>
                  
                  <div className="space-y-2">
                    <Label className="label-uppercase">Professional Background *</Label>
                    <Select 
                      value={formData.professional_background} 
                      onValueChange={(v) => setFormData({...formData, professional_background: v})}
                    >
                      <SelectTrigger data-testid="background-select">
                        <SelectValue placeholder="Select your background" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROFESSIONAL_BACKGROUNDS.map(bg => (
                          <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Organization (Optional)</Label>
                      <Input
                        placeholder="e.g., Local VA Hospital"
                        value={formData.organization}
                        onChange={(e) => setFormData({...formData, organization: e.target.value})}
                        data-testid="organization-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="label-uppercase">Years of Experience</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        placeholder="e.g., 5"
                        value={formData.years_of_experience}
                        onChange={(e) => setFormData({...formData, years_of_experience: e.target.value})}
                        data-testid="experience-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Certifications */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  Certifications & Training
                </h3>
                <p className="text-sm text-muted-foreground">Select any certifications you hold (recommended but not required)</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {CERTIFICATIONS.map(cert => (
                    <div key={cert} className="flex items-center space-x-2">
                      <Checkbox
                        id={cert}
                        checked={formData.certifications.includes(cert)}
                        onCheckedChange={() => handleCertificationToggle(cert)}
                      />
                      <Label htmlFor={cert} className="text-sm cursor-pointer">{cert}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  Availability
                </h3>
                
                <div className="space-y-2">
                  <Label className="label-uppercase">Available Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <Button
                        key={day}
                        type="button"
                        variant={formData.availability_days.includes(day) ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDayToggle(day)}
                        className="text-xs"
                      >
                        {day.slice(0, 3)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="label-uppercase">Available Hours</Label>
                  <Input
                    placeholder="e.g., 9 AM - 5 PM, Evenings, 24/7"
                    value={formData.availability_hours}
                    onChange={(e) => setFormData({...formData, availability_hours: e.target.value})}
                    data-testid="hours-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-uppercase">Zip Code</Label>
                  <Input
                    placeholder="e.g., 20001"
                    maxLength={10}
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    data-testid="zipcode-input"
                  />
                </div>
              </div>

              {/* Bio */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  About You
                </h3>
                <div className="space-y-2">
                  <Label className="label-uppercase">Bio / Why You Want to Help *</Label>
                  <Textarea
                    placeholder={formData.applicant_type === "veteran" 
                      ? "Share your story and why you want to become a responder. This will be visible to veterans..."
                      : "Share your experience working with veterans or in crisis support, and why you want to help..."}
                    className="min-h-[150px]"
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    data-testid="bio-textarea"
                  />
                  <p className="text-xs text-muted-foreground">
                    This bio will be visible to veterans in the responder directory
                  </p>
                </div>
              </div>

              {/* Background Check Consent */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent"
                    checked={formData.background_check_consent}
                    onCheckedChange={(checked) => setFormData({...formData, background_check_consent: checked})}
                    data-testid="consent-checkbox"
                  />
                  <div>
                    <Label htmlFor="consent" className="cursor-pointer font-medium">
                      Background Check Consent *
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      I consent to a background check as part of the responder verification process. 
                      I understand that V.A.L.O.R.™ may verify my information
                      {formData.applicant_type === "veteran" ? " and service record" : " and professional credentials"}.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate("/dashboard")}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-1 bg-primary hover:bg-primary/90"
                  data-testid="submit-application-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResponderApplication;
